import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import foodRepository from '../../repositories/foodRepository';
import { formatPriceWithCurrency } from '../../utils/currencyConverter';
import { useTranslation } from '../../i18n/i18n';
import AlertModal from '../../components/common/AlertModal';
import BackButton from '../../components/page_header/BackButton';
import { getFoodImageUrl, getLocalizedName } from '../../utils/localization';
import './ShopMenuScreen.css';

function ShopMenuScreen() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
 
  const [menuItems, setMenuItems] = useState([]);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [shopId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (shopId) {
          const ref = doc(db, 'shops', shopId);
          const snap = await getDoc(ref);
          if (snap.exists()) setShop({ id: snap.id, ...snap.data() });
        }

        const res = await foodRepository.getMenuItemsByShop(shopId, 50);
        if (res.success) {
          setMenuItems(res.foods);
        } else {
          setError(res.error || 'Failed to fetch shop menu');
        }
      } catch (e) {
        console.error(e);
        setError('Failed to fetch shop menu');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shopId]);

  const handleFoodClick = (food) => navigate(`/food/${food.id}`);
  const shopName = getLocalizedName(shop, lang, shop?.name || `Gate ${shop?.gate || shopId?.slice?.(0,6) || ''}`);

  return (
    <div className="shop-menu-page">
      <div className="shop-menu-header">
        <div className="shop-title-row">
          <BackButton fallbackTo="/home" className="shop-menu-back" />
          <div className="shop-title-container">
            <h2 className="shop-title">{shopName}</h2>
            {shop && shop.shopAvailability === false && (
              <span className="shop-closed-tag">{t('home.closed')}</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="shop-menu-error">{error}</div>
      )}

      <div className="shop-menu-grid">
        {loading ? (
          [...Array(8)].map((_, i) => (
            <div key={i} className="grid-card shimmer">
              <div className="grid-image" />
              <div className="grid-content">
                <div className="line" />
                <div className="line short" />
              </div>
            </div>
          ))
        ) : (
          menuItems.map(food => {
            const foodName = getLocalizedName(food, lang, food.name || '');
            const imageUrl = getFoodImageUrl(food);
            return (
            <div 
              key={food.id} 
              className={`grid-card ${shop && shop.shopAvailability === false ? 'is-closed' : ''}`} 
              onClick={() => {
                if (shop && shop.shopAvailability === false) {
                  setIsAlertOpen(true);
                  return;
                }
                handleFoodClick(food);
              }}
            >
              <div className="grid-image">
                {shop && shop.shopAvailability === false && (
                  <div className="closed-overlay">
                    <span>{t('home.closed')}</span>
                  </div>
                )}
                <img
                  src={imageUrl}
                  alt={foodName}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/assets/images/on-boarding-1.png`;
                  }}
                />
              </div>
              <div className="grid-content">
                <h3 className="grid-name">{foodName}</h3>
                <div className="grid-bottom">
                  <span className="grid-price">{formatPriceWithCurrency(food.price, food.currency)}</span>
                  <span className="grid-prep">{food.getPreparationTimeText()}</span>
                </div>
              </div>
            </div>
          );
          })
        )}
      </div>

      <AlertModal 
        isOpen={isAlertOpen}
        title={t('home.closed')}
        message={t('home.shop_closed_msg')}
        type="warning"
        onClose={() => setIsAlertOpen(false)}
      />
    </div>
  );
}

export default ShopMenuScreen;
